import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaPlay, FaStar, FaCalendarAlt, FaSpinner, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const SimpleTrendingCarousel = () => {
    const [trendingMovies, setTrendingMovies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentSlide, setCurrentSlide] = useState(0);
    const { isDark } = useTheme();

    useEffect(() => {
        const fetchTrendingMovies = async () => {
            try {
                setLoading(true);
                const res = await api.get('/movies/search', {
                    params: {
                        sort_by: 'popularity.desc',
                        from_year: new Date().getFullYear() - 2
                    }
                });
                setTrendingMovies(res.data.slice(0, 5)); // Take top 5
            } catch (err) {
                console.error('Failed to fetch trending movies:', err);
                setError('Failed to load trending movies');
            } finally {
                setLoading(false);
            }
        };

        fetchTrendingMovies();
    }, []);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % trendingMovies.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + trendingMovies.length) % trendingMovies.length);
    };

    useEffect(() => {
        if (trendingMovies.length > 0) {
            const interval = setInterval(nextSlide, 5000);
            return () => clearInterval(interval);
        }
    }, [trendingMovies.length]);

    if (loading) {
        return (
            <div className="relative h-96 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center">
                <div className="text-center text-white">
                    <FaSpinner className="text-4xl animate-spin mx-auto mb-4" />
                    <p>Loading trending movies...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="relative h-96 bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl flex items-center justify-center">
                <div className="text-center text-white">
                    <p className="text-xl">{error}</p>
                </div>
            </div>
        );
    }

    if (trendingMovies.length === 0) {
        return null;
    }

    const currentMovie = trendingMovies[currentSlide];

    return (
        <div className="relative h-64 sm:h-80 md:h-96 lg:h-[500px] rounded-xl sm:rounded-2xl overflow-hidden">
            {/* Background Image */}
            <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000"
                style={{
                    backgroundImage: currentMovie.backdrop_path
                        ? `url(https://image.tmdb.org/t/p/original${currentMovie.backdrop_path})`
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                }}
            >
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 h-full flex items-center">
                <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 w-full">
                    <div className="max-w-2xl">
                        <div className="flex items-center space-x-2 mb-4">
                            <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                                🔥 Trending
                            </span>
                            {currentMovie.vote_average > 0 && (
                                <div className="flex items-center space-x-1 bg-yellow-500 text-yellow-900 px-3 py-1 rounded-full text-sm font-bold">
                                    <FaStar className="text-xs" />
                                    <span>{currentMovie.vote_average.toFixed(1)}</span>
                                </div>
                            )}
                        </div>

                        <h2 className="text-xl sm:text-2xl md:text-4xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 leading-tight">
                            {currentMovie.title}
                        </h2>

                        {currentMovie.overview && (
                            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-4 sm:mb-6 line-clamp-2 sm:line-clamp-3">
                                {currentMovie.overview}
                            </p>
                        )}

                        <div className="flex items-center space-x-6 mb-8">
                            {currentMovie.release_date && (
                                <div className="flex items-center space-x-2 text-white/80">
                                    <FaCalendarAlt />
                                    <span>{new Date(currentMovie.release_date).getFullYear()}</span>
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <Link
                                to={`/movie/${currentMovie.id}`}
                                className="inline-flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base lg:text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                            >
                                <FaPlay className="text-xs sm:text-sm" />
                                <span>View Details</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Buttons */}
            <button
                onClick={prevSlide}
                className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 text-white p-2 sm:p-3 rounded-full transition-all duration-200 backdrop-blur-sm"
            >
                <FaChevronLeft className="text-sm sm:text-xl" />
            </button>

            <button
                onClick={nextSlide}
                className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 text-white p-2 sm:p-3 rounded-full transition-all duration-200 backdrop-blur-sm"
            >
                <FaChevronRight className="text-sm sm:text-xl" />
            </button>

            {/* Dots Indicator */}
            <div className="absolute bottom-3 sm:bottom-6 left-1/2 transform -translate-x-1/2 z-20 flex space-x-1 sm:space-x-2">
                {trendingMovies.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-200 ${index === currentSlide
                            ? 'bg-white'
                            : 'bg-white/50 hover:bg-white/75'
                            }`}
                    />
                ))}
            </div>
        </div>
    );
};

export default SimpleTrendingCarousel;
